import React, { FC } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { ListItemIcon, ListItemText } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";

import {
    BulletListIcon,
    DocumentsNoteIcon,
    LogoutIcon,
    RafterDownIcon,
    SettingsIcon,
    TruckIcon,
    UserIcon,
    UsersIcon,
} from "@/assets/icons";
import { HiddenOnScope } from "@/components/atoms";
import { StyledHeaderMenuButton } from "@/components/layouts/styled";
import { Permissions, UserState } from "@/shared/domain";
import { isABroker, useAuth, useNavigation } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import { StyledAccountMenu } from "./styled";

const AccountInformation: FC = () => {
    const { user, userState } = useAuth();
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const { logout } = useAuth();
    const { navToDocuments, navToSettings, navToAddressChange, navToPolicyholders, navToMyRequests } = useNavigation();

    const hasAccess = userState === UserState.ACCESS;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = (isPreview: boolean) => {
        logout();
        tagManager.pushLogoutEvent();
        if (isPreview) {
            window.close();
        }
    };

    const handleDocuments = () => {
        navToDocuments();
        handleClose();
    };

    const handleSettings = () => {
        navToSettings();
        handleClose();
    };

    const handleProfiles = () => {
        navToPolicyholders();
        handleClose();
    };

    const handleMyRequests = () => {
        navToMyRequests();
        handleClose();
    };

    const handleChangeAddress = () => {
        if (isABroker()) {
            return;
        }
        navToAddressChange();
        handleClose();
    };

    const getLogOutText = () => {
        if (isABroker()) return t("AccountUser.ClosePreview");
        if (user.isScopedLogin()) return t("AccountUser.LogoutScoped");
        return t("AccountUser.LogOut");
    };

    return (
        <>
            <StyledHeaderMenuButton
                aria-controls="customized-menu-account"
                aria-haspopup="true"
                $active={!!anchorEl}
                onClick={handleClick}
                data-testid="account_information-button-account"
                title={t("AccountUser.MyAccount")}
            >
                <div className={clsx("icon", anchorEl && "active")}>
                    <UserIcon />
                    <RafterDownIcon className="arrowIcon" style={!anchorEl ? null : { transform: "rotate(180deg)" }} />
                </div>
            </StyledHeaderMenuButton>
            <StyledAccountMenu
                classes={{ paper: "menu-paper", list: "menu-list" }}
                id="customized-menu-account"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <HiddenOnScope>
                    {hasAccess && user.hasGlobalPermission(Permissions.ViewLinkedDocuments) && (
                        <MenuItem
                            classes={{ root: "menuitem-root" }}
                            className="accountInfoItem"
                            onClick={handleDocuments}
                            data-testid="account_information-option-documents"
                        >
                            <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                                <DocumentsNoteIcon />
                            </ListItemIcon>
                            <ListItemText
                                classes={{ root: "menuitem-text-root" }}
                                primary={t("AccountUser.MyDocuments")}
                            />
                        </MenuItem>
                    )}

                    {hasAccess && (
                        <MenuItem
                            classes={{ root: "menuitem-root" }}
                            className="accountInfoItem"
                            onClick={handleMyRequests}
                            data-testid="account_information-option-requests"
                        >
                            <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                                <BulletListIcon />
                            </ListItemIcon>
                            <ListItemText classes={{ root: "menuitem-text-root" }} primary={t("Common.MyDemands")} />
                        </MenuItem>
                    )}

                    {hasAccess && user.hasGlobalPermission(Permissions.AccessChangeAddress) && (
                        <MenuItem
                            classes={{ root: "menuitem-root" }}
                            className={`accountInfoItem${isABroker() ? " disabled" : ""}`}
                            title={`${isABroker() ? t("Common.PreviewUserNotAllowed") : ""}`}
                            onClick={handleChangeAddress}
                            data-testid="account_information-option-address"
                        >
                            <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                                <TruckIcon />
                            </ListItemIcon>
                            <ListItemText
                                classes={{ root: "menuitem-text-root" }}
                                primary={t("AccountUser.ChangeAddress")}
                            />
                        </MenuItem>
                    )}

                    {hasAccess && user.hasGlobalPermission(Permissions.ViewInformation) && (
                        <MenuItem
                            classes={{ root: "menuitem-root" }}
                            className="accountInfoItem"
                            onClick={handleProfiles}
                            data-testid="account_information-option-folders"
                        >
                            <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                                <UsersIcon />
                            </ListItemIcon>
                            <ListItemText
                                classes={{ root: "menuitem-text-root" }}
                                primary={t("AccountUser.ManageFolders")}
                            />
                        </MenuItem>
                    )}
                </HiddenOnScope>
                <MenuItem
                    classes={{ root: "menuitem-root" }}
                    className="accountInfoItem"
                    onClick={handleSettings}
                    data-testid="account_information-option-settings"
                >
                    <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                        <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText classes={{ root: "menuitem-text-root" }} primary={t("AccountUser.Settings")} />
                </MenuItem>

                <MenuItem
                    classes={{ root: "menuitem-root" }}
                    className="accountInfoItem"
                    onClick={() => handleLogout(isABroker())}
                    data-testid="account_information-option-logout"
                >
                    <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText classes={{ root: "menuitem-text-root" }} primary={getLogOutText()} />
                </MenuItem>
            </StyledAccountMenu>
        </>
    );
};

export default AccountInformation;
